<template>
  <article class="event-page recurring-conference-page">
    <EventHero :event="event" />
    <EventSectionNav :items="navigation" />
    <EventSummary
      :event="event"
      heading="An Annual Reckon Tradition"
      kicker="The re:Invent Story"
    />
    <EventStats
      :items="event.atAGlance"
      title="At a Glance"
      kicker="Four Years in Las Vegas"
      section-id="at-a-glance"
    />
    <RecurringHotelCard :hotel="event.typicalHotel" />
    <RecurringEventYearNav
      :years="event.years"
      :active-year="activeYear"
      @select="scrollToYear"
    />

    <div id="story" class="year-story" aria-label="The re:Invent story by year">
      <RecurringEventYearSection
        v-for="(year, index) in event.years"
        :key="year.year"
        :year="year"
        :index="index"
        :event="event"
      />
    </div>

    <RecurringRestaurantGrid :restaurants="event.restaurantVisits" :event="event" />
    <RecurringActivityGrid :activities="event.activities" :event="event" />
    <RecurringTraditions :traditions="event.traditions" />
    <RecurringCrewGrid :crew="event.recurringCrew" />
    <MeritBadgeSection :badges="event.meritBadges" :event="event" />
    <EventGallery v-if="event.gallery && event.gallery.length" :media="event.gallery" :title="`${event.title} Moments`" />
  </article>
</template>

<script>
import EventHero from '../event/EventHero.vue';
import EventSectionNav from '../event/EventSectionNav.vue';
import EventSummary from '../event/EventSummary.vue';
import EventStats from '../stats/EventStats.vue';
import MeritBadgeSection from '../badges/MeritBadgeSection.vue';
import EventGallery from '../media/EventGallery.vue';
import RecurringEventYearNav from '../recurring/RecurringEventYearNav.vue';
import RecurringEventYearSection from '../recurring/RecurringEventYearSection.vue';
import RecurringRestaurantGrid from '../recurring/RecurringRestaurantGrid.vue';
import RecurringActivityGrid from '../recurring/RecurringActivityGrid.vue';
import RecurringTraditions from '../recurring/RecurringTraditions.vue';
import RecurringCrewGrid from '../recurring/RecurringCrewGrid.vue';
import RecurringHotelCard from '../recurring/RecurringHotelCard.vue';

export default {
  name: 'RecurringConferenceEventPage',
  components: {
    EventHero,
    EventSectionNav,
    EventSummary,
    EventStats,
    MeritBadgeSection,
    EventGallery,
    RecurringEventYearNav,
    RecurringEventYearSection,
    RecurringRestaurantGrid,
    RecurringActivityGrid,
    RecurringTraditions,
    RecurringCrewGrid,
    RecurringHotelCard,
  },
  props: { event: { type: Object, required: true } },
  data() {
    return {
      activeYear: this.event.years[0]?.year || null,
      yearObserver: null,
    };
  },
  computed: {
    navigation() {
      return [
        { id: 'overview', label: 'Overview', show: true },
        { id: 'at-a-glance', label: 'At a Glance', show: this.event.atAGlance?.length },
        { id: 'hotel', label: 'Hotel', show: this.event.typicalHotel },
        { id: 'story', label: 'Years', show: this.event.years?.length },
        { id: 'dining', label: 'Dining', show: this.event.restaurantVisits?.length },
        { id: 'activities', label: 'Adventures', show: this.event.activities?.length },
        { id: 'traditions', label: 'Traditions', show: this.event.traditions?.length },
        { id: 'crew', label: 'Crew', show: this.event.recurringCrew?.length },
        { id: 'badges', label: 'Badges', show: this.event.meritBadges?.length },
        { id: 'gallery', label: 'Gallery', show: this.event.gallery?.length },
      ].filter((item) => item.show);
    },
  },
  mounted() {
    this.$nextTick(this.observeYears);
  },
  beforeDestroy() {
    if (this.yearObserver) this.yearObserver.disconnect();
  },
  methods: {
    scrollToYear(year) {
      const target = document.getElementById(`year-${year}`);
      if (!target) return;

      this.activeYear = year;
      window.history.replaceState(null, '', `#year-${year}`);
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    observeYears() {
      if (!('IntersectionObserver' in window)) return;

      this.yearObserver = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible) this.activeYear = Number(visible.target.id.replace('year-', ''));
      }, { rootMargin: '-25% 0px -58% 0px', threshold: [0.05, 0.25, 0.5] });

      this.event.years.forEach((year) => {
        const section = document.getElementById(`year-${year.year}`);
        if (section) this.yearObserver.observe(section);
      });
    },
  },
};
</script>

<style scoped>
.event-page { background: #050505; }
.year-story { scroll-margin-top: 68px; }
</style>
